import { supabase } from './supabase';

const HEARTBEAT_INTERVAL_MS = 60_000;
const IP_FETCH_TIMEOUT_MS = 5000;
const STORAGE_KEY = 'active_session_id';

let heartbeatTimer: ReturnType<typeof setInterval> | null = null;
let unloadListener: (() => void) | null = null;
let visibilityListener: (() => void) | null = null;
let currentSessionId: string | null = null;
let inFlightStart: Promise<string | null> | null = null;

type DeviceInfo = {
  device_type: string;
  os: string;
  browser: string;
  user_agent: string;
};

type IpInfo = {
  ip: string | null;
  city: string | null;
  region: string | null;
  country: string | null;
};

function detectDevice(): DeviceInfo {
  const ua = navigator.userAgent || '';
  const uaLower = ua.toLowerCase();

  let device_type = 'desktop';
  if (/mobile|android|iphone|ipod/i.test(ua) && !/ipad|tablet/i.test(ua)) {
    device_type = 'mobile';
  } else if (/ipad|tablet/i.test(ua)) {
    device_type = 'tablet';
  }

  let os = 'Desconhecido';
  if (uaLower.includes('windows')) os = 'Windows';
  else if (uaLower.includes('android')) os = 'Android';
  else if (uaLower.includes('iphone') || uaLower.includes('ipad') || uaLower.includes('ipod')) os = 'iOS';
  else if (uaLower.includes('mac os x') || uaLower.includes('macintosh')) os = 'macOS';
  else if (uaLower.includes('linux')) os = 'Linux';

  let browser = 'Desconhecido';
  if (uaLower.includes('edg/')) browser = 'Edge';
  else if (uaLower.includes('opr/') || uaLower.includes('opera')) browser = 'Opera';
  else if (uaLower.includes('chrome/') && !uaLower.includes('chromium')) browser = 'Chrome';
  else if (uaLower.includes('firefox/')) browser = 'Firefox';
  else if (uaLower.includes('safari/') && !uaLower.includes('chrome')) browser = 'Safari';

  return { device_type, os, browser, user_agent: ua };
}

async function fetchIpInfoWithTimeout(): Promise<IpInfo> {
  const emptyResult: IpInfo = { ip: null, city: null, region: null, country: null };
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), IP_FETCH_TIMEOUT_MS);
    const response = await fetch('https://ipapi.co/json/', {
      method: 'GET',
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    if (!response.ok) return emptyResult;
    const body = await response.json();
    return {
      ip: body.ip ?? null,
      city: body.city ?? null,
      region: body.region ?? null,
      country: body.country_name ?? body.country ?? null,
    };
  } catch {
    return emptyResult;
  }
}

function clearTimers() {
  if (heartbeatTimer) {
    clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  if (unloadListener) {
    window.removeEventListener('pagehide', unloadListener);
    window.removeEventListener('beforeunload', unloadListener);
    unloadListener = null;
  }
  if (visibilityListener) {
    document.removeEventListener('visibilitychange', visibilityListener);
    visibilityListener = null;
  }
}

async function sendHeartbeat(sessionId: string) {
  try {
    const { error } = await supabase.rpc('record_session_heartbeat', { p_session_id: sessionId });
    if (error) console.warn('heartbeat rpc error', error);
  } catch (err) {
    console.warn('heartbeat failed', err);
  }
}

async function endSessionOnUnload(sessionId: string) {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (!token) return;
    const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/rpc/record_session_end`;
    await fetch(url, {
      method: 'POST',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ p_session_id: sessionId }),
    });
  } catch {
    // best effort only
  }
}

function attachLifecycleListeners(sessionId: string) {
  clearTimers();

  heartbeatTimer = setInterval(() => {
    if (document.visibilityState !== 'hidden') {
      sendHeartbeat(sessionId);
    }
  }, HEARTBEAT_INTERVAL_MS);

  visibilityListener = () => {
    if (document.visibilityState === 'visible') {
      sendHeartbeat(sessionId);
    }
  };
  document.addEventListener('visibilitychange', visibilityListener);

  unloadListener = () => {
    endSessionOnUnload(sessionId);
  };
  window.addEventListener('pagehide', unloadListener);
  window.addEventListener('beforeunload', unloadListener);
}

async function insertSession(): Promise<string | null> {
  const device = detectDevice();

  const { data, error } = await supabase.rpc('record_session_start', {
    p_ip: null,
    p_city: null,
    p_region: null,
    p_country: null,
    p_device_type: device.device_type,
    p_os: device.os,
    p_browser: device.browser,
    p_user_agent: device.user_agent,
  });

  if (error) {
    console.warn('failed to start session tracking', error);
    return null;
  }

  const sessionId = data as string;

  fetchIpInfoWithTimeout()
    .then(async (ipInfo) => {
      if (!ipInfo.ip && !ipInfo.city) return;
      try {
        await supabase
          .from('user_sessions')
          .update({
            ip: ipInfo.ip,
            city: ipInfo.city,
            region: ipInfo.region,
            country: ipInfo.country,
          })
          .eq('id', sessionId);
      } catch (err) {
        console.warn('failed to attach ip info to session', err);
      }
    })
    .catch(() => {
      // ignored — session is already recorded
    });

  return sessionId;
}

export async function startSessionTracking(): Promise<string | null> {
  if (inFlightStart) return inFlightStart;
  if (currentSessionId) return currentSessionId;

  inFlightStart = (async () => {
    const sessionId = await insertSession();
    if (!sessionId) {
      return null;
    }
    currentSessionId = sessionId;
    try {
      localStorage.setItem(STORAGE_KEY, sessionId);
    } catch {
      // ignore
    }
    attachLifecycleListeners(sessionId);
    return sessionId;
  })();

  try {
    const result = await inFlightStart;
    return result;
  } finally {
    inFlightStart = null;
  }
}

export async function endSessionTracking() {
  const sessionId = currentSessionId || (() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();

  clearTimers();
  currentSessionId = null;

  if (sessionId) {
    try {
      await supabase.rpc('record_session_end', { p_session_id: sessionId });
    } catch (err) {
      console.warn('failed to end session tracking', err);
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }
}

export async function resumeSessionTracking(): Promise<string | null> {
  if (currentSessionId) return currentSessionId;

  const existing = (() => {
    try {
      return localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  })();

  if (existing) {
    const { error } = await supabase.rpc('record_session_heartbeat', { p_session_id: existing });
    if (!error) {
      currentSessionId = existing;
      attachLifecycleListeners(existing);
      return existing;
    }
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  return startSessionTracking();
}
